import { gql, useQuery } from "@apollo/client";
import Button from "components/base/Button";
import XLSX from "xlsx";

const WIS_COST_PRICES = gql`
  query PreviewTotalByHSCodeWisCostPrices($shipmentId: ID!) {
    commercialInvoicePrices(shipmentId: $shipmentId, priceSource: "wis") {
      rowId
      unitPrice
      error
    }
  }
`;

const PreviewTotalByHSCodeView = ({ shipment }) => {
  const { data, loading } = useQuery(WIS_COST_PRICES, {
    variables: { shipmentId: shipment.id },
  });
  const prices = Object.fromEntries(
    (data?.commercialInvoicePrices || []).map((item) => [item.rowId, item]),
  );
  const lines = shipment.shipmentInvoices.flatMap((shipmentInvoice) =>
    shipmentInvoice.shipmentInvoiceRows.map((shipmentInvoiceRow) => ({
      ...shipmentInvoiceRow,
      hsCode: shipmentInvoiceRow.hsCodeForDestination?.trim() || "-",
      totalValue:
        prices[shipmentInvoiceRow.id]?.unitPrice * shipmentInvoiceRow.qty,
    })),
  );

  const uniqueHSCodes = [...new Set(lines.map((line) => line.hsCode))];

  const hsCodeLines = uniqueHSCodes.map((hsCode) => {
    const totalQty = lines
      .filter((line) => line.hsCode === hsCode)
      .reduce((acc, line) => acc + line.qty, 0);
    const totalNetWeight = lines
      .filter((line) => line.hsCode === hsCode)
      .reduce((acc, line) => acc + line.totalNetWeight, 0);
    const totalGrossWeight = lines
      .filter((line) => line.hsCode === hsCode)
      .reduce((acc, line) => acc + line.totalGrossWeight, 0);
    const hsCodeLines = lines.filter((line) => line.hsCode === hsCode);
    return {
      hsCode,
      lines: hsCodeLines,
      totalQty,
      totalNetWeight,
      totalGrossWeight,
      totalValue: hsCodeLines.reduce(
        (total, line) => total + (line.totalValue || 0),
        0,
      ),
      hasMissingPrice: hsCodeLines.some(
        (line) => !prices[line.id] || prices[line.id].error,
      ),
    };
  });

  function exportExcel() {
    let data = [];
    for (let i = 0; i < hsCodeLines.length; i++) {
      const hsCodeLine = hsCodeLines[i];
      data.push([
        hsCodeLine.hsCode,
        "Items / Package",
        "QTY",
        "Total N.W.",
        "Total G.W.",
        "Total Value",
      ]);
      for (let j = 0; j < hsCodeLine.lines.length; j++) {
        const line = hsCodeLine.lines[j];
        data.push([
          line.productName,
          line.product?.itemsPerSet || line.qtyPerCarton,
          line.qty,
          line.totalNetWeight.toFixed(2),
          line.totalGrossWeight.toFixed(2),
          line.totalValue?.toFixed(2) || "-",
        ]);
      }
      data.push([
        "Total",
        "",
        hsCodeLine.totalQty,
        hsCodeLine.totalNetWeight.toFixed(2),
        hsCodeLine.totalGrossWeight.toFixed(2),
        hsCodeLine.hasMissingPrice ? "-" : hsCodeLine.totalValue.toFixed(2),
      ]);
      data.push([]);
    }
    const sheet = XLSX.utils.json_to_sheet(data);
    var range = XLSX.utils.decode_range(sheet["!ref"]);
    range.s.r = 1; // <-- zero-indexed, so setting to 1 will skip row 0
    sheet["!ref"] = XLSX.utils.encode_range(range);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, sheet, "Shipment_Products_by_HS_Code");
    XLSX.writeFile(wb, "Shipment_Products_by_HS_Code.xlsx");
  }

  return (
    <div>
      <div className="mb-4 text-sm">Based on WIS cost.</div>
      <table className="mt-4">
        {hsCodeLines.map((hsCodeLine) => (
          <tbody key={hsCodeLine.hsCode}>
            <tr className="border-y dark:border-gray-700 bg-gray-200 dark:bg-gray-900 dark:bg-opacity-60">
              <td>{hsCodeLine.hsCode}</td>
              <td className="text-right">Items / Package</td>
              <td className="text-right">QTY</td>
              <td className="text-right">Total N.W.</td>
              <td className="text-right">Total G.W.</td>
              <td className="text-right">Total Value</td>
            </tr>
            {hsCodeLine.lines.map((line) => (
              <tr key={line.id}>
                <td>{line.productName}</td>
                <td className="text-right">
                  {line.product?.itemsPerSet || line.qtyPerCarton}
                </td>
                <td className="text-right">{line.qty}</td>
                <td className="text-right">{line.totalNetWeight.toFixed(2)}</td>
                <td className="text-right">
                  {line.totalGrossWeight.toFixed(2)}
                </td>
                <td className="text-right">
                  {loading ? "Loading..." : line.totalValue?.toFixed(2) || "-"}
                </td>
              </tr>
            ))}
            <tr className="font-semibold border-t dark:border-gray-700">
              <td className="py-3">Total</td>
              <td></td>
              <td className="text-right">{hsCodeLine.totalQty}</td>
              <td className="text-right">
                {hsCodeLine.totalNetWeight.toFixed(2)}
              </td>
              <td className="text-right">
                {hsCodeLine.totalGrossWeight.toFixed(2)}
              </td>
              <td className="text-right">
                {loading || hsCodeLine.hasMissingPrice
                  ? "-"
                  : hsCodeLine.totalValue.toFixed(2)}
              </td>
            </tr>
          </tbody>
        ))}
      </table>

      <hr />

      <div className="flex justify-end">
        <Button disabled={loading} onClick={exportExcel}>
          Download
        </Button>
      </div>
    </div>
  );
};

export default PreviewTotalByHSCodeView;
